import styled, { css } from 'styled-components'
import { Notification, tokens } from '@arvoreeducacao/bonsai-ds'
import Search from 'components/lib/inputs/Search'

export const LogoArvore = styled.img`
  margin-right: ${tokens.spacing.horizontalXxsmall};
  margin-left: ${tokens.spacing.horizontalXsmall};
  cursor: pointer;
`

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 ${tokens.spacing.horizontalXsmall};
  width: 100%;
`

export const SearchWrapper = styled.div`
  margin-right: ${tokens.spacing.horizontalMedium};
  margin-left: ${tokens.spacing.horizontalXsmall};
`

export const MenuWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  > div {
    display: flex;
    align-items: center;
  }
`

export const UserSubmenuWrapper = styled.div`
  position: relative;
`

export const UserPill = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 16px 4px 4px;
  border: 0;
  border-radius: 999px;
  background: ${({ $open }) => ($open ? tokens.colors.dark1 : 'transparent')};
  font-family: inherit;
  font-size: 16px;
  font-weight: ${tokens.fontWeights.bold};
  color: ${tokens.colors.dark6};
  cursor: pointer;
  outline: 0;
  transition: background ${tokens.transitions.default};

  &:hover {
    background: ${tokens.colors.dark1};
  }

  &:focus-visible {
    outline: 2px solid ${tokens.colors.dark6};
    outline-offset: 2px;
  }
`

export const UserPillAvatar = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 4px;
  border-radius: 50%;
  background: ${tokens.colors.primary1};
  font-size: 14px;
  font-weight: ${tokens.fontWeights.bold};
  color: ${tokens.colors.dark6};
`

export const DropdownStyled = styled.div`
  position: absolute;
  top: 58px;
  width: max-content;
  border-radius: ${tokens.radius.large};
  border: solid 1px ${tokens.colors.dark2};
  right: 0;
  z-index: 2;
  background: ${tokens.colors.white};
  box-shadow: ${tokens.shadows.shadowDown6};

  li:last-child {
    border-bottom: none;
  }

  li:focus-visible {
    outline: 2px solid ${tokens.colors.dark6};
    outline-offset: -2px;
  }

  button:focus-visible {
    outline: 2px solid ${tokens.colors.dark6};
    outline-offset: 2px;
  }
`

export const DropdownSearchStyled = styled.div`
  position: absolute;
  top: 82px;
  width: 436px;
  border-radius: ${tokens.radius.xlarge};
  border: solid 1px ${tokens.colors.dark2};
  right: 100px;
  z-index: 10;
  background: ${tokens.colors.white};
  box-shadow: ${tokens.shadows.shadowDown6};

  [role='info-block'],
  [data-testid='info-block'] {
    height: 400px !important;
  }

  p {
    width: 400px;
  }
`

export const OverlaySearch = styled.div<{ showOverlay?: boolean }>`
  position: fixed;
  top: 70px;
  left: 0px;
  width: 100vw;
  height: 100%;
  min-height: 100vh;
  background: ${tokens.overlay.intense};
  overflow: scroll;
  z-index: 10;
  display: flex;
  flex-direction: 'row';
  justify-content: 'center';
  align-items: 'flex-start';
  opacity: ${props => (props.showOverlay ? 1 : 0)};
  visibility: 'hidden';
  transition: ${tokens.transitions.default};
`

export const SearchStyled = styled(Search)`
  > div {
    border-radius: ${tokens.radius.radius_50}px;
    background-color: ${tokens.colors.dark1};

    :focus-within {
      border: 2px solid ${tokens.colors.primary900};
      background-color: ${tokens.colors.livrosLight};
    }

    > input {
      padding: ${tokens.spacing.spacingXSmall}px
        ${tokens.spacing.spacingSmall}px;
      height: 40px;
      font-size: ${tokens.fontSize.medium};
      color: ${tokens.colors.dark5};

      &::placeholder {
        color: ${tokens.colors.dark5};
      }

      &:focus-visible {
        outline: none;
      }
    }

    > svg {
      margin-right: 15px !important;
      color: ${tokens.colors.dark5} !important;

      width: 16px !important;
      height: 16px !important;
      min-width: 16px !important;
      min-height: 16px !important;
    }
  }
`

export const NavigationItemWrapper = styled.div<{
  hasBadge?: boolean
  compact?: boolean
}>`
  [data-testid='bonsai-icon-leaf-outline'] {
    scale: 1.7;
    margin-bottom: 15px;
  }

  margin-right: ${tokens.spacing.horizontalXxsmall};
  position: ${props => (props.hasBadge ? 'relative' : 'static')};

  ${props =>
    props.compact &&
    css`
      @media (max-width: 1440px) {
        [data-testid='item-text'],
        [data-testid='first-spacing'] {
          display: none;
        }
      }
    `}
`

export const NavigationItemContainer = styled.div`
  position: relative;
  display: inline-block;

  [data-testid='bonsai-icon-clipboard-content'],
  [data-testid='bonsai-icon-clipboard-content-filled'] {
    position: relative;
  }
`

export const ActivitiesBadge = styled.div<{ a11yEnabled?: boolean }>`
  position: absolute;
  top: 25px;
  left: 12px;
  width: 16px;
  height: 16px;
  background-color: ${({ a11yEnabled }) =>
    a11yEnabled ? tokens.colors.error200 : tokens.colors.red3};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: ${tokens.fontWeights.bold};
  color: ${({ a11yEnabled }) =>
    a11yEnabled ? tokens.colors.error900 : tokens.colors.white};
  z-index: 1;
  line-height: 1;
  transform: translate(50%, -50%);
`

export const UserDropdownContainer = styled.div`
  position: absolute;
  min-width: 195px;
  right: 16px;
  top: 59px;
  z-index: 10;
`

export const HeaderNotificationWrapper = styled.div`
  display: flex;
  align-items: center;
`

export const NotificationStyled = styled(Notification)`
  position: relative;
  left: 90%;
  bottom: 40px;
`

export const NotificationStyledProfile = styled(Notification)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
`

export const NotificationStyledProfileWrapper = styled.div`
  position: relative;

  > button:focus-visible {
    outline: 2px solid ${tokens.colors.dark6};
    outline-offset: 2px;
  }
`

export const HelpButtonLoading = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  height: 48px;
  box-sizing: border-box;
  opacity: 0.5;
  cursor: wait;
  pointer-events: none;
  border-bottom: 1px solid ${tokens.colors.dark2};

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid ${tokens.colors.dark3};
    border-top-color: ${tokens.colors.dark6};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  span {
    font-size: 14px;
    color: ${tokens.colors.dark6};
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`
